import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAppliedList } from "../api/services/candidateApply";
import { useSuperApp } from "./useSuperApp";
import { useCallback, useMemo } from "react";

const APPLIED_JOBS_STORAGE_PREFIX = "applied_jobs";

/**
 * A stable per-user suffix for anything kept in localStorage, so two people sharing a
 * device never read each other's state. Exported because the notifications store keys its
 * own state the same way — one definition, or the two would drift and silently swap.
 */
export const getUserKey = (user) => {
  if (!user) return null;
  if (user.id) return String(user.id);
  if (user._id) return String(user._id);
  if (user.phone_number) return `phone_${String(user.phone_number).replace(/\D/g, "")}`;
  if (user.phone) return `phone_${String(user.phone).replace(/\D/g, "")}`;
  if (user.phoneNo) {
    const country = user.countryCode ? String(user.countryCode).replace(/\D/g, "") : "";
    const phone = String(user.phoneNo).replace(/\D/g, "");
    return `phone_${country}${phone}`;
  }
  return null;
};

const getStorageKey = (user) => {
  const userKey = getUserKey(user);
  return userKey ? `${APPLIED_JOBS_STORAGE_PREFIX}:${userKey}` : null;
};

const readStoredAppliedJobs = (storageKey) => {
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((id) => {
        const parsedId = Number(id);
        return Number.isNaN(parsedId) ? null : parsedId;
      })
      .filter((id) => id !== null);
  } catch {
    return [];
  }
};

const writeStoredAppliedJobs = (storageKey, ids) => {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(ids));
  } catch {
    // Ignore storage errors (e.g., quota)
  }
};

/**
 * Custom hook for tracking applied jobs with React Query caching
 * - Only fetches when user is authenticated
 * - Caches results for 5 minutes
 * - Provides optimistic update function for immediate UI feedback
 */
export function useAppliedJobs() {
  const queryClient = useQueryClient();
  const { isAuthenticated, isAuthLoading, currentUser } = useSuperApp();
  const authUser = currentUser;
  const userKey = getUserKey(authUser);
  const storageKey = getStorageKey(authUser);
  const appliedJobsKey = ['appliedJobs', userKey || 'anonymous'];

  const { data: appliedJobs = [] } = useQuery({
    queryKey: appliedJobsKey,
    queryFn: async () => {
      const storedIds = readStoredAppliedJobs(storageKey);

      // Fetch from hrSystemServer (CV submissions with CV_SENT stage)
      let hrServerIds = [];
      try {
        const hrResult = await getAppliedList();
        if (hrResult.success && hrResult.data) {
          const rawData = Array.isArray(hrResult.data) ? hrResult.data : [];
          hrServerIds = rawData
            .map(item => {
              const rawId = item.assessmentId ?? item.jobId ?? item.id;
              const parsedId = rawId != null ? Number(rawId) : NaN;
              return Number.isNaN(parsedId) ? null : parsedId;
            })
            .filter((id) => id !== null);
        }
      } catch (e) {
        console.error('Error fetching applied list from hrSystemServer:', e);
      }

      // Merge sources: localStorage + hrSystemServer
      const merged = Array.from(new Set([...storedIds, ...hrServerIds]));
      writeStoredAppliedJobs(storageKey, merged);
      return merged;
    },
    enabled: isAuthenticated && !isAuthLoading,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,
  });

  // Memoized Set for O(1) lookup
  const appliedJobIds = useMemo(() => new Set(appliedJobs), [appliedJobs]);

  // Optimistic update - instantly add to cache after successful apply
  const addAppliedJob = useCallback((jobId) => {
    const parsedId = jobId != null ? Number(jobId) : NaN;
    if (Number.isNaN(parsedId)) return;
    queryClient.setQueryData(appliedJobsKey, (old = []) => {
      if (old.includes(parsedId)) return old;
      const next = [...old, parsedId];
      writeStoredAppliedJobs(storageKey, next);
      return next;
    });
  }, [appliedJobsKey, queryClient, storageKey]);

  return {
    appliedJobIds,
    addAppliedJob,
    isAuthenticated
  };
}

export default useAppliedJobs;
